import {
	TArgumentsForGetCustomersShort,
	ICreateCustomer,
	IUpdateCustomer,
	IGetPaginatedCustomers,
	ICreateCustomerLocalization,
	IUpdateCustomerLocalization,
	IUpdateCustomerStyles,
	IGetPaginatedCustomerSoftwareUpdates,
	IPushNotification,
	IUpdateCustomerBPA,
} from 'types';
import {
	serviceGetCustomerInfo,
	serviceGetCustomersShort,
	serviceCreateCustomer,
	serviceUpdateCustomer,
	serviceGetPaginatedCustomers,
	serviceDeleteCustomer,
	serviceArchiveCustomer,
	serviceRestoreCustomer,
	serviceGetCustomerLocalizationInfo,
	serviceCreateCustomerLocalization,
	serviceUpdateCustomerLocalization,
	serviceGetCustomerLanguages,
	serviceGetCustomerStylesInfo,
	serviceUpdateCustomerStylesInfo,
	serviceGetLatestCustomerSoftwareUpdatesData,
	servicePushCustomerNotification,
	serviceGetPaginatedCustomerSoftwareUpdates,
	serviceGetCustomerBPA,
	serviceUpdateCustomerBPA,
	serviceGetCustomerBPAShort,
} from 'services/api/customers';
import { createAppErrorMessage } from 'utils/helpers';

const getCustomerInfo = async (customerID: string) => {
	try {
		const response = await serviceGetCustomerInfo(customerID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getCustomersShortInfo = async (data: TArgumentsForGetCustomersShort) => {
	try {
		const response = await serviceGetCustomersShort(data);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const createCustomer = async (data: ICreateCustomer) => {
	try {
		await serviceCreateCustomer(data);
	} catch (err) {
		throw err;
	}
};

const updateCustomer = async (data: IUpdateCustomer) => {
	try {
		await serviceUpdateCustomer(data);
	} catch (err) {
		throw err;
	}
};

const getPaginatedCustomers = async (args: IGetPaginatedCustomers) => {
	try {
		const response = await serviceGetPaginatedCustomers(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const deleteCustomer = async (customerID: string) => {
	try {
		await serviceDeleteCustomer(customerID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorDeletingCustomer');
		throw new Error(errorMessage);
	}
};

const archiveCustomer = async (customerID: string) => {
	try {
		await serviceArchiveCustomer(customerID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorArchivingCustomer');
		throw new Error(errorMessage);
	}
};

const restoreCustomer = async (customerID: string) => {
	try {
		await serviceRestoreCustomer(customerID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorRestoringCustomer');
		throw new Error(errorMessage);
	}
};

const getCustomerLocalizationInfo = async (customerID: string) => {
	try {
		const response = await serviceGetCustomerLocalizationInfo(customerID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const createCustomerLocalization = async (data: ICreateCustomerLocalization) => {
	try {
		await serviceCreateCustomerLocalization(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingCustomerLocalization');
		throw new Error(errorMessage);
	}
};

const updateCustomerLocalization = async (data: IUpdateCustomerLocalization) => {
	try {
		await serviceUpdateCustomerLocalization(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingCustomerLocalization');
		throw new Error(errorMessage);
	}
};

const getCustomerLanguages = async (customerID: string) => {
	try {
		const response = await serviceGetCustomerLanguages(customerID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const getCustomerStylesInfo = async (customerID: string) => {
	try {
		const response = await serviceGetCustomerStylesInfo(customerID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const updateCustomerStyles = async (data: IUpdateCustomerStyles) => {
	try {
		await serviceUpdateCustomerStylesInfo(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingCustomerStyles');
		throw new Error(errorMessage);
	}
};

const getLatestCustomerSoftwareUpdatesData = async (customerID: string) => {
	try {
		const response = await serviceGetLatestCustomerSoftwareUpdatesData(customerID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const pushCustomerNotification = async (customerID: string, data: IPushNotification) => {
	try {
		await servicePushCustomerNotification(customerID, data);
	} catch (err) {
		throw err;
	}
};

const getPaginatedCustomerSoftwareUpdates = async (args: IGetPaginatedCustomerSoftwareUpdates) => {
	try {
		const response = await serviceGetPaginatedCustomerSoftwareUpdates(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getCustomerBPA = async (customerID: string) => {
	try {
		const response = await serviceGetCustomerBPA(customerID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const updateCustomerBPA = async (data: IUpdateCustomerBPA) => {
	try {
		await serviceUpdateCustomerBPA(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingCustomerBPA');
		throw new Error(errorMessage);
	}
};

const getCustomerBPAShort = async (customerID: string) => {
	try {
		const response = await serviceGetCustomerBPAShort(customerID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

export {
	getCustomerInfo,
	getCustomersShortInfo,
	createCustomer,
	updateCustomer,
	getPaginatedCustomers,
	deleteCustomer,
	archiveCustomer,
	restoreCustomer,
	getCustomerLocalizationInfo,
	createCustomerLocalization,
	updateCustomerLocalization,
	getCustomerLanguages,
	getCustomerStylesInfo,
	updateCustomerStyles,
	getLatestCustomerSoftwareUpdatesData,
	pushCustomerNotification,
	getPaginatedCustomerSoftwareUpdates,
	getCustomerBPA,
	updateCustomerBPA,
	getCustomerBPAShort,
};
